import { getTokenFromLocal, handleResponse } from '../../_helpers/handleResponseMiddleware';

const centralBaseUrl = process.env.REACT_APP_CENTRAL_BASE_URL;

/**
 * Returns auth headers for AR Reconciliation API calls.
 * Token is always read dynamically from localStorage — never hardcoded.
 */
function arAuthHeaders() {
  const user = getTokenFromLocal();
  const token = user?.access_token || null;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    appname: 'Hospital Web Chat',
  };
}

/**
 * 1. Fetch AR Status Data for a hospital.
 * @param {Object} params
 * @param {string} params.hospitalId         - Hospital unique identifier (required)
 * @param {string} [params.hospitalStatus]   - 'approve' | 'dispute' | omit for open/pending cases
 */
export function getArStatusDataService({ hospitalId, hospitalStatus } = {}) {
  const query = new URLSearchParams();
  query.append('hospitalId', hospitalId);
  if (hospitalStatus) query.append('hospitalStatus', hospitalStatus);

  return fetch(`${centralBaseUrl}/getArStatusData?${query.toString()}`, {
    method: 'GET',
    headers: arAuthHeaders(),
  })
    .then(handleResponse)
    .catch((error) => {
      console.error('getArStatusDataService error:', error);
      throw error;
    });
}

/**
 * 2. Fetch Detailed Appointment Bill Information by appId.
 * @param {string} appId - Appointment ID
 */
export function getAppointmentBillInfoService(appId) {
  return fetch(`${centralBaseUrl}/getAppointmentBillInfo?appId=${encodeURIComponent(appId)}`, {
    method: 'GET',
    headers: arAuthHeaders(),
  })
    .then(handleResponse)
    .catch((error) => {
      console.error('getAppointmentBillInfoService error:', error);
      throw error;
    });
}

/**
 * 3. Update AR Status (Sign Off or Raise Dispute).
 * @param {Object} payload
 * @param {string} payload.appId            - Appointment ID
 * @param {string} payload.status           - 'hospital_approve' | 'hospital_dispute'
 * @param {string} [payload.remark]         - Reason / notes (required for dispute)
 * @param {number} [payload.arap_at_hospital] - AR/AP at Hospital number (for dispute)
 * @param {string} [payload.dispute_bucket] - Dispute category (for dispute)
 * @param {Array}  [payload.files]          - Array of uploaded file objects [{ url }]
 */
export function updateArStatusService({
  appId,
  hospitalId,
  status,
  hospitalStatus,
  remark = '',
  arap_at_hospital,
  dispute_bucket,
  files = [],
} = {}) {
  const resolvedStatus = status || hospitalStatus;
  const isApprove = resolvedStatus === 'hospital_approve' || resolvedStatus === 'approve';

  const body = isApprove
    ? {
        appId,
        hospitalId,
        hospitalStatus: 'hospital_approve',
      }
    : {
        appId,
        hospitalId,
        hospitalStatus: 'hospital_dispute',
        remark,
        arap_at_hospital: Number(arap_at_hospital) || 0,
        dispute_bucket: dispute_bucket || '',
        ...(Array.isArray(files) && files.length ? { files } : {}),
      };

  return fetch(`${centralBaseUrl}/updateArStatus`, {
    method: 'POST',
    headers: arAuthHeaders(),
    body: JSON.stringify(body),
  })
    .then(handleResponse)
    .catch((error) => {
      console.error('updateArStatusService error:', error);
      throw error;
    });
}

/**
 * 4. Fetch AR Enabled Hospital List.
 */
export function getArHospitalListService() {
  return fetch(`${centralBaseUrl}/getArHospitalList`, {
    method: 'GET',
    headers: arAuthHeaders(),
  })
    .then(handleResponse)
    .catch((error) => {
      console.error('getArHospitalListService error:', error);
      throw error;
    });
}

